import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Brand } from '../../components/Brand.tsx';
import { useI18n } from '../../i18n/index.tsx';

/**
 * The privacy notice, as a page anybody can reach without a session.
 *
 * A rater opens this in the middle of deciding whether to be candid about a
 * colleague, so it is written to answer one question — who reads what I write —
 * rather than to satisfy a contract. The full version for the client's legal
 * review, with the citations to where each claim is implemented, lives in
 * `docs/aviso-de-privacidad.md`; this is the same content in the product's
 * voice.
 *
 * Deliberately outside the shell and outside the survey: it opens in its own
 * tab, so nobody loses a half-finished questionnaire to read it.
 */

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="mt-9 border-t border-line-soft pt-6">
    <h2 className="text-[19px] font-bold tracking-[-0.02em]">{title}</h2>
    <div className="mt-3 flex flex-col gap-3 text-[15px] leading-relaxed text-soft">
      {children}
    </div>
  </section>
);

/** A numbered rule. The count is what carries the argument, so it is visible. */
const Rule = ({ n, children }: { n: number; children: ReactNode }) => (
  <li className="flex gap-3 border-t border-line-soft py-2.5 first:border-t-0">
    <span className="w-5 shrink-0 font-mono text-[13px] font-bold text-teal">
      {String(n).padStart(2, '0')}
    </span>
    <span>{children}</span>
  </li>
);

export const PrivacyPage = () => {
  const { t } = useI18n();

  const readers = [
    { who: t('privacy.whoYou'), sees: t('privacy.whoYouSees'), never: t('privacy.whoYouNever') },
    {
      who: t('privacy.whoSubject'),
      sees: t('privacy.whoSubjectSees'),
      never: t('privacy.whoSubjectNever'),
    },
    {
      who: t('privacy.whoCoach'),
      sees: t('privacy.whoCoachSees'),
      never: t('privacy.whoCoachNever'),
    },
    { who: t('privacy.whoHr'), sees: t('privacy.whoHrSees'), never: t('privacy.whoHrNever') },
  ];

  const [rightsBefore = '', rightsAfter = ''] = t('privacy.rightsBody').split('{contact}');

  const comments = [
    t('privacy.commentsStep1'),
    t('privacy.commentsStep2'),
    t('privacy.commentsStep3'),
    t('privacy.commentsStep4'),
  ];

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-5 pb-16">
      <Brand />

      <main className="flex-1 py-6">
        <h1 className="text-[28px] leading-[1.1] font-extrabold tracking-[-0.025em]">
          {t('privacy.title')}
        </h1>
        <p className="mt-2 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
          {t('privacy.subtitle')}
        </p>

        {/* The summary is the whole notice for most readers; everything below
            is the same promise with its workings shown. */}
        <div className="mt-6 rounded-2xl border border-tint-line bg-tint-soft px-5 py-4">
          <p className="font-mono text-[10px] tracking-[0.13em] text-teal-deep uppercase">
            {t('privacy.summaryTitle')}
          </p>
          <p className="mt-2 text-[15.5px] leading-relaxed">{t('privacy.summary')}</p>
          <p className="mt-3 text-[14px] leading-relaxed text-soft">
            {t('privacy.summaryException')}
          </p>
        </div>

        <Section title={t('privacy.collectedTitle')}>
          <p>{t('privacy.collectedIdentity')}</p>
          <p>{t('privacy.collectedAnswers')}</p>
          <p>{t('privacy.collectedProcess')}</p>
        </Section>

        <Section title={t('privacy.whoTitle')}>
          <p>{t('privacy.whoLead')}</p>

          <div className="-mx-1 overflow-x-auto">
            <table className="mt-1 w-full min-w-[520px] border-collapse text-left text-[14px]">
              <thead>
                <tr className="font-mono text-[9.5px] tracking-[0.12em] text-faint uppercase">
                  <th className="w-[24%] border-b border-line py-2 pr-3 font-normal">
                    {t('privacy.whoColumnReader')}
                  </th>
                  <th className="border-b border-line px-3 py-2 font-normal">
                    {t('privacy.whoColumnSees')}
                  </th>
                  <th className="border-b border-line py-2 pl-3 font-normal">
                    {t('privacy.whoColumnNever')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {readers.map((reader) => (
                  <tr key={reader.who} className="align-top">
                    <td className="border-b border-line-soft py-3 pr-3 font-semibold text-ink">
                      {reader.who}
                    </td>
                    <td className="border-b border-line-soft px-3 py-3">{reader.sees}</td>
                    <td className="border-b border-line-soft py-3 pl-3">{reader.never}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p>{t('privacy.whoHrNote')}</p>
        </Section>

        <Section title={t('privacy.rulesTitle')}>
          <p>{t('privacy.rulesLead')}</p>
          <ul className="list-none text-[14.5px] text-ink">
            <Rule n={1}>{t('privacy.rule1')}</Rule>
            <Rule n={2}>{t('privacy.rule2')}</Rule>
            <Rule n={3}>{t('privacy.rule3')}</Rule>
            <Rule n={4}>{t('privacy.rule4')}</Rule>
          </ul>
          <p>{t('privacy.rulesComments')}</p>
        </Section>

        {/* The one place the promise is narrower than a rater might assume, so
            it gets a heading of its own rather than a line in a paragraph. */}
        <Section title={t('privacy.supervisorTitle')}>
          <p className="text-ink">{t('privacy.supervisorBody')}</p>
          <p>{t('privacy.supervisorWhy')}</p>
          <p>{t('privacy.supervisorNotice')}</p>
        </Section>

        <Section title={t('privacy.commentsTitle')}>
          <ul className="list-none text-[14.5px] text-ink">
            {comments.map((step, index) => (
              <Rule key={step} n={index + 1}>
                {step}
              </Rule>
            ))}
          </ul>
          <p>{t('privacy.commentsAi')}</p>
        </Section>

        <Section title={t('privacy.residualTitle')}>
          <p>{t('privacy.residualLead')}</p>
          <ul className="ml-4 list-disc marker:text-faint">
            <li className="mt-1.5">{t('privacy.residual1')}</li>
            <li className="mt-1.5">{t('privacy.residual2')}</li>
            <li className="mt-1.5">{t('privacy.residual3')}</li>
          </ul>
        </Section>

        <Section title={t('privacy.rightsTitle')}>
          <p className="text-ink">{t('privacy.rightsPurpose')}</p>
          {/* The address is a live mailto, so the sentence is split around the
              placeholder rather than interpolated into a string. */}
          <p>
            {rightsBefore}
            <a
              href={`mailto:${t('privacy.contactEmail')}`}
              className="font-semibold text-teal underline underline-offset-2"
            >
              {t('privacy.contactEmail')}
            </a>
            {rightsAfter}
          </p>
          <p>{t('privacy.rightsSecurity')}</p>
        </Section>

        <p className="mt-9 border-t border-line-soft pt-5 text-[12.5px] text-faint">
          {t('privacy.footer', { controller: t('privacy.controller') })}
        </p>

        <Link
          to="/"
          className="mt-6 inline-block font-mono text-[10px] tracking-[0.12em] text-soft uppercase transition hover:text-teal"
        >
          {t('privacy.back')}
        </Link>
      </main>
    </div>
  );
};
